import React, { useEffect, useId, useState } from 'react';
import axios from 'axios';
import API_BASE from './apiBase';
import { createSessionCacheKey, readSessionCache, writeSessionCache } from './sessionDataCache';
import { formatCompactNumber } from './numberFormatUtils';
import { maskEmail, maskPhone } from './maskUtils';
import {
  Clipboard, CheckCircle, XCircle, User, Briefcase, Users, Bell,
  ArrowRight, ChevronRight, MessageSquare, BarChart2, ShoppingBag,
  Clock, RefreshCw, Undo2, RotateCcw,
} from 'lucide-react';

/* ─── Design tokens ──────────────────────────── */
const T = {
  blue900: '#0a1628',
  blue800: '#0d1f3c',
  blue700: '#0f3460',
  blue600: '#1a56a4',
  blue500: '#2563eb',
  blue400: '#3b82f6',
  blue300: '#60a5fa',
  blue200: '#93c5fd',
  blue100: '#dbeafe',
  blue50: '#eff6ff',
  white: '#ffffff',
  slate: '#64748b',
  slate200: '#e2e8f0',
  slate100: '#f1f5f9',
  green: '#059669',
  greenLight: '#34d399',
  red: '#dc2626',
  redLight: '#f87171',
  amber: '#d97706',
  amberLight: '#fbbf24',
  purple: '#7c3aed',
  purpleLight: '#a78bfa',
};

/* Shared blue — Page Guide modal / buttons use this */
const SHARED_ACCENT = T.blue500;
const SHARED_GRADIENT = `linear-gradient(135deg, ${T.blue500} 0%, #1d4ed8 100%)`;
const SHARED_CHIP_BORDER = '#bfdbfe';
const SHARED_CHIP_BG = '#eff6ff';

/* Each category now has its own color */
const mkRole = (accent, dark, glow, chipBg, chipBorder) => ({
  accent,
  glow,
  gradient: `linear-gradient(135deg, ${accent} 0%, ${dark} 100%)`,
  chipBg,
  chipBorder,
});

const ROLE_CONFIG = {
  Employee: {
    icon: <User size={22} strokeWidth={2.2} />,
    description: 'Staff consents',
    display: 'Employees',
    ...mkRole('#2563eb', '#1d4ed8', 'rgba(37,99,235,0.22)', '#eff6ff', '#bfdbfe'),
  },
  Vendor: {
    icon: <Briefcase size={22} strokeWidth={2.2} />,
    description: 'Supplier agreements',
    display: 'Vendors & Suppliers',
    ...mkRole('#4f46e5', '#3730a3', 'rgba(79,70,229,0.22)', '#eef2ff', '#c7d2fe'),
  },
  Partners: {
    icon: <Users size={22} strokeWidth={2.2} />,
    description: 'Partnership records',
    display: 'Partners',
    ...mkRole('#0891b2', '#0e7490', 'rgba(8,145,178,0.22)', '#ecfeff', '#a5f3fc'),
  },
  Customer: {
    icon: <ShoppingBag size={22} strokeWidth={2.2} />,
    description: 'Customer agreements',
    display: 'Customers',
    ...mkRole('#7c3aed', '#6d28d9', 'rgba(124,58,237,0.22)', '#f5f3ff', '#ddd6fe'),
  },
};

/* Single source of truth for status colors + icons */
const STATUS_META = {
  total:     { label: 'Total',       color: '#2563eb', soft: '#eff6ff', icon: Clipboard },
  accepted:  { label: 'Accepted',    color: '#059669', soft: '#ecfdf5', icon: CheckCircle },
  pending:   { label: 'Pending',     color: '#ea580c', soft: '#fff7ed', icon: Clock },
  declined:  { label: 'Declined',    color: '#dc2626', soft: '#fef2f2', icon: XCircle },
  reconsent: { label: 'Reconsent',   color: '#ca8a04', soft: '#fefce8', icon: RefreshCw },
  revoked:   { label: 'Revoked',     color: '#64748b', soft: '#f1f5f9', icon: Undo2 },
  expiring:  { label: 'Expiry soon', color: '#7c3aed', soft: '#f5f3ff', icon: Bell },
  expired:   { label: 'Expired',     color: '#e11d48', soft: '#fff1f2', icon: Clock },
};
const MIX_ORDER = ['accepted', 'pending', 'declined', 'reconsent', 'revoked'];

const STAT_BUTTONS = ['total', 'accepted', 'pending', 'declined', 'reconsent', 'revoked', 'expiring', 'expired']
  .map((key) => ({ key, ...STATUS_META[key] }));

const GUIDE_STEPS = [
  { icon: '🛡️', title: 'Consent Overview Banner', body: 'Track, manage, and review all active consent records across your organization from this centralized dashboard.', target: '.co-header-banner' },
  { icon: '📊', title: 'Overall Status Metrics', body: 'Click any metric card (Total, Accepted, Pending, Declined, Reconsent, Revoked, Expiry Soon) to instantly filter records by that status.', target: '.co-header-banner' },
  { icon: '🗂️', title: 'Consent Categories', body: 'Consents are organized into Employee, Vendor, Partner, and Customer categories for structured role-based management.', target: '.co-section-title' },
  { icon: '👥', title: 'Category Breakdown Cards', body: 'Each card shows a status bar plus live counts for Accepted, Pending, Declined, Reconsent, Revoked, and Expiring consents. Click any card to expand its detailed consent types.', target: '.co-cards-grid' },
  { icon: '📖', title: 'Interactive Page Guide', body: 'Click the Page Guide button anytime to replay this tour and explore all features.', target: '.co-header-banner button' },
];

/* ── Helper: get tenant id safely ─────────────── */
function getTenantId(user) {
  try {
    const candidates = [user?.tenant_id, user?.tenantId, user?.tenant, localStorage.getItem('tenant_id')];
    for (const candidate of candidates) {
      const tid = Number(candidate);
      if (Number.isFinite(tid) && tid > 0) return tid;
    }
    return null;
  } catch (e) {
    return null;
  }
}

/* ── Helper: check if record belongs to current tenant ── */
function matchesTenant(r, tenantId) {
  if (!tenantId) return true; // no tenant filter
  const rTenant = Number(r.tenant_id || r.tenantId || r.tenant || 0);
  return rTenant === tenantId;
}

/* ── Helper: determine role ───────────────────── */
function roleOf(r) {
  const entityVal = String((r.entity || '')).toLowerCase();
  const fields = [r.role, r.user_role, r.type, r.consent_type, r.category, r.template, r.entity];
  const v = String((fields.filter(Boolean).join(' ') || '')).toLowerCase();
  if (!v && !entityVal) return null;
  const employeeKeys = ['employee', 'staff', 'member', 'employeeid', 'emp'];
  const vendorKeys = ['vendor', 'supplier', 'vendorid', 'vendor_name', 'vendorcompany'];
  const partnerKeys = ['partner', 'partners', 'partnerid', 'affiliate'];
  const customerKeys = ['customer', 'client', 'customerid', 'buyer', 'consumer'];
  if (entityVal) {
    if (employeeKeys.some(k => entityVal.includes(k))) return 'Employee';
    if (vendorKeys.some(k => entityVal.includes(k))) return 'Vendor';
    if (partnerKeys.some(k => entityVal.includes(k))) return 'Partners';
    if (customerKeys.some(k => entityVal.includes(k))) return 'Customer';
  }
  if (employeeKeys.some(k => v.includes(k))) return 'Employee';
  if (vendorKeys.some(k => v.includes(k))) return 'Vendor';
  if (partnerKeys.some(k => v.includes(k))) return 'Partners';
  if (customerKeys.some(k => v.includes(k))) return 'Customer';
  return null;
}

/* ── Helper: determine status ─────────────────── */
function statusOf(r) {
  const v = String((r.status || r.consent_status || r.state || '') || '').toLowerCase();
  if (v.includes('delet')) return 'deleted';
  if (v.includes('revok')) return 'revoked';
  if (v.includes('declin') || v.includes('reject')) return 'declined';
  if (v.includes('reconsent')) return 'reconsent';
  if (v.includes('expir') || v.includes('ended')) return 'expired';
  if (v.includes('inactive') || v.includes('in-active') || v.includes('deactiv') || v.includes('unaccept') || v.includes('nonaccept') || v.includes('not accept') || v.includes('not active') || v.includes('not approv') || v.includes('not grant') || v.includes('not agree') || v.includes('disagree')) return 'pending';
  if (v.includes('accept') || v.includes('agree') || v === 'active' || v === 'approved' || v === 'granted' || v === 'yes') return 'accepted';
  if (v.includes('pend')) return 'pending';
  return null;
}

function isAcceptedConsent(r) {
  const status = String(r?.status || r?.consent_status || r?.state || '').toLowerCase();
  return statusOf({ status }) === 'accepted';
}

function daysUntilExpiry(r) {
  if (r?.days_to_expiry !== undefined && r?.days_to_expiry !== null) {
    const days = Number(r.days_to_expiry);
    return Number.isNaN(days) ? null : days;
  }
  if (r?.expires_at) {
    const expiryDate = new Date(r.expires_at);
    return Number.isNaN(expiryDate.getTime()) ? null : (expiryDate.getTime() - Date.now()) / 86400000;
  }
  const expiryDays = Number(r?.expiry ?? r?.expiring_days);
  const createdAt = r?.created_at ? new Date(r.created_at) : null;
  if (Number.isNaN(expiryDays) || !createdAt || Number.isNaN(createdAt.getTime())) return null;
  return (createdAt.getTime() + expiryDays * 86400000 - Date.now()) / 86400000;
}

/* ── Helper: extract consent message ─────────── */
function getConsentMessage(r) {
  if (!r) return null;
  // r.consent is the primary field returned by the backend /user/consent API
  return r.consent || r.consent_message || r.consent_text ||
    r.message || r.description || r.message_text ||
    r.request_details || r.details || r.text || null;
}

function emptyStats() {
  return { total: 0, accepted: 0, declined: 0, deleted: 0, revoked: 0, pending: 0, reconsent: 0, expiring: 0, expired: 0 };
}

export function buildRoleStatsFromRows(rows, tenantId = null) {
  const result = { Employee: emptyStats(), Vendor: emptyStats(), Partners: emptyStats(), Customer: emptyStats() };

  for (const row of rows || []) {
    if (!matchesTenant(row, tenantId)) continue;

    const role = roleOf(row);
    if (!role) continue;

    const target = result[role] || (result[role] = emptyStats());
    target.total += 1;

    const status = statusOf(row);
    if (status && status !== 'expired') {
      target[status] = (target[status] || 0) + 1;
    }

    const daysToExpiry = daysUntilExpiry(row);
    if (isAcceptedConsent(row) && (row.expiring_soon === true || (daysToExpiry > 0 && daysToExpiry <= 7))) {
      target.expiring = (target.expiring || 0) + 1;
    }
    if (isAcceptedConsent(row) && daysToExpiry !== null && daysToExpiry <= 0) {
      target.expired = (target.expired || 0) + 1;
    }
  }

  return result;
}

/* ── Sparkline ────────────────────────────────── */
function sparkPath(data, w = 200, h = 48, pad = 5) {
  const max = Math.max(...data, 1), min = Math.min(...data, 0), r = max - min || 1;
  const p = data.map((v, i) => [(i / (data.length - 1)) * w, h - pad - ((v - min) / r) * (h - pad * 2)]);
  let d = `M${p[0][0]},${p[0][1]}`;
  for (let i = 0; i < p.length - 1; i++) {
    const a = p[i - 1] || p[i], b = p[i], c = p[i + 1], e = p[i + 2] || c;
    d += ` C${b[0] + (c[0] - a[0]) / 6},${b[1] + (c[1] - a[1]) / 6} ${c[0] - (e[0] - b[0]) / 6},${c[1] - (e[1] - b[1]) / 6} ${c[0]},${c[1]}`;
  }
  return { line: d, area: `${d} L${w},${h} L0,${h} Z` };
}
const dummyWave = (n) => Array.from({ length: 7 }, (_, i) => n * (0.6 + 0.4 * Math.sin(i + n)));

function Sparkline({ data, color }) {
  const id = useId().replace(/:/g, '');
  const { line, area } = sparkPath(data);
  return (
    <svg viewBox="0 0 200 48" preserveAspectRatio="none" aria-hidden="true"
         style={{ width: '100%', height: 44, display: 'block' }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.26" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* ── Stat Buttons (top row) ───────────────────── */
function StatButtons({ stats, loading, onStatClick, trends = {} }) {
  const total = Number(stats?.total || 0);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, width: '100%' }}>
      {STAT_BUTTONS.map((s) => {
        const Icon = s.icon;
        const value = Number(stats?.[s.key] ?? 0);
        const pct = total ? Math.round((value / total) * 100) : 0;
        return (
          <button
            key={s.key}
            title={value.toLocaleString()}
            onClick={(e) => { e.stopPropagation(); onStatClick?.(s.key); }}
            style={{
              position: 'relative', overflow: 'hidden', textAlign: 'left',
              display: 'flex', flexDirection: 'column',
              background: '#fff', border: `1px solid ${T.slate200}`, borderRadius: 16,
              padding: 0, cursor: 'pointer', outline: 'none',
              boxShadow: '0 1px 4px -1px rgba(15,23,42,0.06)',
              transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.borderColor = s.color;
              e.currentTarget.style.boxShadow = `0 10px 24px -10px ${s.color}`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = T.slate200;
              e.currentTarget.style.boxShadow = '0 1px 4px -1px rgba(15,23,42,0.06)';
            }}
          >
            <span style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 3, background: s.color, opacity: 0.55 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 14px 0' }}>
              <span style={{ width: 30, height: 30, borderRadius: '50%', background: s.soft, color: s.color,
                             display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={15} strokeWidth={2.4} />
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: T.slate, whiteSpace: 'nowrap' }}>{s.label}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '12px 14px 6px' }}>
              <span style={{ fontSize: 30, fontWeight: 800, color: T.blue900, lineHeight: 1, letterSpacing: '-0.03em' }}>
                {loading ? (
                  <span style={{ display: 'inline-block', width: 34, height: 24, borderRadius: 6,
                    background: 'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',
                    backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
                ) : formatCompactNumber(value)}
              </span>
              {s.key !== 'total' && !loading && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: s.soft, color: s.color }}>
                  {pct}%
                </span>
              )}
            </div>
            <Sparkline data={trends[s.key] || dummyWave(value)} color={s.color} />
          </button>
        );
      })}
    </div>
  );
}

/* ── Consent Card ─────────────────────────────── */
function ConsentCard({ role, stats, loading, onClick, onFilterClick, onViewConsents }) {
  const cfg = ROLE_CONFIG[role];
  const [hovered, setHovered] = useState(false);
  const total = Number(stats?.total || 0);
  const chipKeys = ['accepted', 'pending', 'declined', 'reconsent'];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(role)}
      onKeyDown={(e) => e.key === 'Enter' && onClick(role)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative', background: '#fff', borderRadius: 20,
        border: `1.5px solid ${hovered ? cfg.accent : '#e2e8f0'}`,
        overflow: 'hidden', cursor: 'pointer', height: '100%',
        boxShadow: hovered ? `0 16px 40px -8px ${cfg.glow}` : '0 1px 6px -1px rgba(15,23,42,0.05)',
        transition: 'all 0.24s cubic-bezier(0.34,1.3,0.64,1)',
        transform: hovered ? 'translateY(-5px)' : 'translateY(0)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Compact gradient header: icon + title on the left, total on the right */}
      <div style={{ background: cfg.gradient, padding: '14px 16px', position: 'relative', overflow: 'hidden',
                    display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ position: 'absolute', top: -34, right: -22, width: 96, height: 96, borderRadius: '50%', background: 'rgba(255,255,255,0.09)', pointerEvents: 'none' }} />
        <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(255,255,255,0.20)', border: '1px solid rgba(255,255,255,0.28)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0, position: 'relative' }}>
          {cfg.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.78)', lineHeight: 1.2 }}>{cfg.description}</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', letterSpacing: '-0.01em', lineHeight: 1.25,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={cfg.display || role}>
            {cfg.display || role}
          </div>
        </div>
        <div
          role="button" tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onFilterClick?.('total'); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onFilterClick?.('total'); } }}
          title={`${total.toLocaleString()} total`}
          style={{ position: 'relative', background: 'rgba(255,255,255,0.20)', border: '1px solid rgba(255,255,255,0.28)', borderRadius: 10,
                   padding: '4px 12px', textAlign: 'center', cursor: 'pointer', minWidth: 52, flexShrink: 0 }}
        >
          <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>
            {loading ? '—' : formatCompactNumber(total)}
          </div>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.85)', lineHeight: 1.2 }}>Total</div>
        </div>
      </div>

      {/* Status mix bar */}
      <div style={{ padding: '14px 14px 0' }}>
        <div role="img" aria-label={`Status mix for ${cfg.display}`}
             style={{ display: 'flex', height: 8, borderRadius: 99, overflow: 'hidden', background: '#f1f5f9' }}>
          {!loading && MIX_ORDER.map((k) => {
            const w = total ? ((stats?.[k] || 0) / total) * 100 : 0;
            return w ? <span key={k} title={`${STATUS_META[k].label}: ${stats[k]}`} style={{ width: `${w}%`, background: STATUS_META[k].color }} /> : null;
          })}
        </div>
      </div>

      {/* Chips: Accepted / Pending / Declined / Reconsent */}
      <div style={{ padding: '12px 14px 0', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
        {chipKeys.map((key) => {
          const m = STATUS_META[key];
          const Icon = m.icon;
          return (
            <button
              key={key}
              onClick={(e) => { e.stopPropagation(); onFilterClick?.(key); }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '9px 2px',
                       background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10, cursor: 'pointer', outline: 'none',
                       transition: 'all 0.15s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = m.color; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#eef2f7'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <span style={{ width: 24, height: 24, borderRadius: '50%', background: m.soft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon size={12} strokeWidth={2.5} color={m.color} />
              </span>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', lineHeight: 1 }} title={Number(stats?.[key] ?? 0).toLocaleString()}>
                {loading ? '—' : formatCompactNumber(stats?.[key] ?? 0)}
              </span>
              <span style={{ fontSize: 10, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>{m.label}</span>
            </button>
          );
        })}
      </div>

      {/* Revoked + expiry metrics */}
      <div style={{ padding: '10px 16px 0', display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11.5, color: '#64748b' }}>
        {['revoked', 'expiring', 'expired'].map((key) => (
          <button
            key={key}
            onClick={(e) => { e.stopPropagation(); onFilterClick?.(key); }}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11.5, color: '#64748b' }}
          >
            {STATUS_META[key].label}{' '}
            <b style={{ color: STATUS_META[key].color }}>{loading ? '—' : formatCompactNumber(stats?.[key] ?? 0)}</b>
          </button>
        ))}
      </div>

      {/* Footer CTA */}
      <div style={{ padding: '14px 16px 16px', marginTop: 'auto' }}>
        <button
          onClick={(e) => { e.stopPropagation(); onViewConsents?.(role); }}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%',
            padding: '10px 17px', borderRadius: 10,
            background: hovered ? cfg.accent : cfg.chipBg, color: hovered ? '#fff' : cfg.accent,
            border: `1.5px solid ${hovered ? cfg.accent : cfg.chipBorder}`,
            fontSize: 13, fontWeight: 600, transition: 'all 0.20s ease', cursor: 'pointer', outline: 'none',
          }}
        >
          View consents
          <ArrowRight size={14} strokeWidth={2.5}
            style={{ transition: 'transform 0.20s ease', transform: hovered ? 'translateX(3px)' : 'translateX(0)' }} />
        </button>
      </div>
    </div>
  );
}

/* ── Consent Type Detail Panel ────────────────── */
function ConsentTypeDetailPanel({ role, typeVal, rows, tenantId, onSelect, onClose, typeStats }) {
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG['Employee'];

  // Compute stats for this role+type, filtered by tenant
  const calculatedStats = { total: 0, accepted: 0, declined: 0, pending: 0, revoked: 0, reconsent: 0 };
  let msgRecord = null;

  for (const r of rows || []) {
    if (!matchesTenant(r, tenantId)) continue;
    const rol = roleOf(r);
    if (!rol || rol !== role) continue;
    const rType = (r.type || r.consent_type || r.category || 'Unknown');
    if (rType !== typeVal) continue;

    calculatedStats.total++;
    const st = statusOf(r);
    if (st && calculatedStats.hasOwnProperty(st)) calculatedStats[st]++;
    // prefer a record that has a non-empty consent message
    if (!msgRecord || (!getConsentMessage(msgRecord) && getConsentMessage(r))) msgRecord = r;
  }

  const stats = typeStats || calculatedStats;
  const consentMessage = getConsentMessage(msgRecord);

  // Unique users for preview (deduplicated by identity)
  const seen = new Set();
  const userItems = [];
  for (const r of rows || []) {
    if (!matchesTenant(r, tenantId)) continue;
    const rol = roleOf(r);
    if (!rol || rol !== role) continue;
    const rType = (r.type || r.consent_type || r.category || 'Unknown');
    if (rType !== typeVal) continue;
    const identity = String(r.email || r.phone || r.username || r.name || r.user_id || '').trim();
    if (seen.has(identity)) continue;
    seen.add(identity);
    userItems.push(r);
    if (userItems.length >= 8) break;
  }

  const statConfigs = [
    { key: 'total', label: 'Total', color: '#1f2937', bg: '#f3f4f6', border: '#d1d5db', icon: Clipboard },
    { key: 'accepted', label: 'Accepted', color: '#059669', bg: '#f0fdf4', border: '#86efac', icon: CheckCircle },
    { key: 'declined', label: 'Declined', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', icon: XCircle },
    { key: 'reconsent', label: 'Reconsent', color: '#b45309', bg: '#fff7ed', border: '#fcd34d', icon: RefreshCw },
    { key: 'pending', label: 'Pending', color: '#d97706', bg: '#fffbeb', border: '#fcd34d', icon: Clock },
    { key: 'revoked', label: 'Revoked', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', icon: Undo2 },
  ];

  const roleNorm = role.toLowerCase().replace(/s$/, '');

  return (
    <div style={{
      background: T.white,
      borderRadius: 18,
      border: `1.5px solid ${T.blue100}`,
      boxShadow: '0 4px 24px -4px rgba(37,99,235,0.10)',
      overflow: 'hidden',
      animation: 'co-fade-up 0.3s ease both',
      marginTop: 8,
    }}>
      {/* Panel header */}
      <div style={{
        background: cfg.gradient,
        padding: '16px 22px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'rgba(255,255,255,0.20)',
            border: '1px solid rgba(255,255,255,0.28)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#ffffff',
          }}>
            <Clipboard size={16} strokeWidth={2.3} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>
              Consent type details
            </div>
            <div style={{ fontFamily: "'Sora', sans-serif", fontSize: 17, fontWeight: 800, color: '#ffffff', letterSpacing: '-0.01em' }}>
              {typeVal}
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            padding: '7px 14px', borderRadius: 9,
            border: '1.5px solid rgba(255,255,255,0.30)',
            background: 'rgba(255,255,255,0.15)', color: '#ffffff',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
        >
          Close ✕
        </button>
      </div>

      <div style={{ padding: '20px 22px' }}>
        {/* 1. Consent Message — shown FIRST */}
        {consentMessage ? (
          <div style={{
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            border: `1.5px solid #7dd3fc`,
            borderRadius: 14,
            padding: '16px 18px',
            marginBottom: 20,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: 'rgba(14,165,233,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <MessageSquare size={14} strokeWidth={2.3} color="#0284c7" />
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0369a1' }}>
                Consent message
              </div>
            </div>
            {/* NOTE: sanitize with DOMPurify before using in production */}
            <div
              style={{ fontSize: 14, color: '#0f172a', lineHeight: 1.7, fontWeight: 500 }}
              dangerouslySetInnerHTML={{ __html: consentMessage }}
            />
          </div>
        ) : (
          <div style={{
            background: '#f8fafc',
            border: '1.5px dashed #cbd5e1',
            borderRadius: 14,
            padding: '14px 18px',
            marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <MessageSquare size={16} strokeWidth={2} color="#94a3b8" />
            <span style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>
              No consent message available for this type.
            </span>
          </div>
        )}

        {/* 2. Stats */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <BarChart2 size={15} strokeWidth={2.3} color={cfg.accent} />
            <span style={{ fontFamily: "'Sora', sans-serif", fontSize: 13, fontWeight: 800, color: T.blue900, letterSpacing: '-0.01em' }}>
              Statistics
            </span>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: 10,
          }}>
            {statConfigs.map(sc => {
              const Icon = sc.icon;
              return (
                <button
                  key={sc.key}
                  onClick={() => {
                    if (sc.key === 'total') {
                      onSelect?.({ role: roleNorm, type: typeVal, dedupe: true });
                    } else {
                      onSelect?.({ role: roleNorm, type: typeVal, status: sc.key, dedupe: true });
                    }
                  }}
                  style={{
                    background: sc.bg,
                    border: `1.5px solid ${sc.border}`,
                    borderRadius: 12,
                    padding: '14px 10px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    outline: 'none',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 6px 16px -4px ${sc.border}`;
                    e.currentTarget.style.borderColor = sc.color;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = sc.border;
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
                    <Icon size={14} strokeWidth={2.3} color={sc.color} />
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: sc.color, lineHeight: 1, letterSpacing: '-0.02em', marginBottom: 5 }} title={Number(stats[sc.key] ?? 0).toLocaleString()}>
                    {formatCompactNumber(stats[sc.key])}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: sc.color }}>
                    {sc.label}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* 3. Users list */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontFamily: "'Sora', sans-serif", fontSize: 13, fontWeight: 800, color: T.blue900, letterSpacing: '-0.01em' }}>
              Users — {typeVal}
            </div>
            <button
              onClick={() => onSelect?.({ role: roleNorm, type: typeVal, dedupe: true })}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', borderRadius: 9,
                background: cfg.accent, color: T.white,
                border: 'none', fontSize: 12, fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              View all <ArrowRight size={13} strokeWidth={2.5} />
            </button>
          </div>

          <div style={{ background: T.blue50, borderRadius: 12, border: `1px solid ${T.blue100}`, overflow: 'hidden' }}>
            {userItems.length === 0 ? (
              <div style={{ padding: '18px 16px', color: T.slate, fontSize: 14, textAlign: 'center' }}>
                No entries found.
              </div>
            ) : (
              userItems.map((it, idx) => {
                const st = statusOf(it);
                const stColors = {
                  accepted: { bg: '#f0fdf4', color: '#059669', label: 'Accepted' },
                  declined: { bg: '#fef2f2', color: '#dc2626', label: 'Declined' },
                  revoked: { bg: '#f1f5f9', color: '#64748b', label: 'Revoked' },
                  reconsent: { bg: '#fefce8', color: '#ca8a04', label: 'Reconsent' },
                  pending: { bg: '#fff7ed', color: '#ea580c', label: 'Pending' },
                };
                const stCfg = stColors[st] || { bg: '#f1f5f9', color: '#64748b', label: st || 'Unknown' };
                const displayName = (it.username || it.Name || it.name || (it.email ? maskEmail(it.email) : '') || (it.phone ? maskPhone(it.phone) : '') || '—').toString().replace(/_/g, ' ').trim();
                const initials = displayName.charAt(0).toUpperCase();
                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 16px',
                      borderBottom: idx < userItems.length - 1 ? `1px solid ${T.blue100}` : 'none',
                      gap: 12,
                      transition: 'background 0.12s ease',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#e0f2fe'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: cfg.gradient,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: T.white, fontSize: 12, fontWeight: 800, flexShrink: 0,
                      }}>
                        {initials}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.blue900 }}>
                          {displayName}
                        </div>
                        {it.email && (
                          <div style={{ fontSize: 11, color: T.slate, fontWeight: 500 }}>{maskEmail(it.email)}</div>
                        )}
                        {it.phone && (
                          <div style={{ fontSize: 11, color: T.slate, fontWeight: 500 }}>{maskPhone(it.phone)}</div>
                        )}
                      </div>
                    </div>
                    <div style={{
                      padding: '3px 10px', borderRadius: 20,
                      background: stCfg.bg, color: stCfg.color,
                      fontSize: 11, fontWeight: 700,
                      border: `1px solid ${stCfg.color}33`,
                      whiteSpace: 'nowrap',
                    }}>
                      {stCfg.label}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Consent Types Panel (for a role) ────────── */
function ConsentTypesPanel({ role, rows, tenantId, onSelect, onClose, typeStats = null, inModal = false }) {
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG['Employee'];
  const [selectedType, setSelectedType] = useState(null);
  const [selectedTypeRows, setSelectedTypeRows] = useState([]);
  const [typeRowsLoading, setTypeRowsLoading] = useState(false);
  const roleNorm = role.toLowerCase().replace(/s$/, '');

  const fallbackTypeMap = {};
  if (!Array.isArray(typeStats)) {
    for (const row of rows || []) {
      if (!matchesTenant(row, tenantId) || roleOf(row) !== role) continue;
      const typeLabel = row.type || row.consent_type || row.category || 'Unknown';
      fallbackTypeMap[typeLabel] = (fallbackTypeMap[typeLabel] || 0) + 1;
    }
  }
  const typeEntries = Array.isArray(typeStats)
    ? typeStats.map(item => [item.type, item.total])
    : Object.entries(fallbackTypeMap).sort((a, b) => b[1] - a[1]);

  useEffect(() => {
    let active = true;
    setSelectedTypeRows([]);
    if (!selectedType || !tenantId) return () => { active = false; };

    setTypeRowsLoading(true);
    const params = { tenant_id: tenantId, dashboard: true, role: roleNorm, page: 1, page_size: 8 };
    if (selectedType !== 'Unknown') params.dashboard_type = selectedType;

    axios.get(`${API_BASE}/user/consent`, { params, skipLoading: true })
      .then(res => {
        if (active) setSelectedTypeRows(Array.isArray(res.data?.consents) ? res.data.consents : []);
      })
      .catch(() => {
        if (!active) return;
        setSelectedTypeRows((rows || []).filter(row =>
          matchesTenant(row, tenantId) && roleOf(row) === role &&
          String(row.type || row.consent_type || row.category || 'Unknown') === selectedType
        ));
      })
      .finally(() => {
        if (active) setTypeRowsLoading(false);
      });

    return () => { active = false; };
  }, [selectedType, role, roleNorm, tenantId, rows]);

  return (
    <div style={{
      marginTop: inModal ? 0 : 24,
      background: T.white,
      borderRadius: 18,
      border: `1.5px solid ${T.blue100}`,
      boxShadow: '0 4px 24px -4px rgba(37,99,235,0.10)',
      overflow: 'hidden',
      animation: 'co-fade-up 0.3s ease both',
    }}>
      {/* Panel header — hidden when rendered inside modal (modal has its own header) */}
      {!inModal && (
        <div style={{
          background: 'linear-gradient(90deg, #eff6ff 0%, #dbeafe 100%)',
          borderBottom: `1.5px solid ${T.blue100}`,
          padding: '16px 22px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 9,
              background: cfg.accent, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: T.white,
            }}>
              {cfg.icon}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.blue400 }}>
                Consent types
              </div>
              <div style={{ fontFamily: "'Sora', sans-serif", fontSize: 16, fontWeight: 800, color: T.blue900, letterSpacing: '-0.01em' }}>
                {cfg.display || role}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '7px 14px', borderRadius: 9,
              border: `1.5px solid ${T.blue200}`,
              background: T.white, color: T.blue600,
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = cfg.accent; e.currentTarget.style.color = T.white; e.currentTarget.style.borderColor = cfg.accent; }}
            onMouseLeave={e => { e.currentTarget.style.background = T.white; e.currentTarget.style.color = T.blue600; e.currentTarget.style.borderColor = T.blue200; }}
          >
            Close &#10005;
          </button>
        </div>
      )}

      {/* Type chips */}
      <div style={{ padding: '18px 22px' }}>
        {typeEntries.length === 0 ? (
          <div style={{ color: T.slate, fontSize: 14, padding: '8px 0' }}>No consent types found for this role.</div>
        ) : (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {typeEntries.map(([typeVal, cnt]) => {
              const isActive = selectedType === typeVal;
              return (
                <button
                  key={typeVal}
                  onClick={() => setSelectedType(isActive ? null : typeVal)}
                  style={{
                    padding: '12px 16px',
                    borderRadius: 12,
                    background: isActive ? cfg.chipBg : '#fff',
                    border: `1.5px solid ${isActive ? cfg.accent : '#e2e8f0'}`,
                    cursor: 'pointer',
                    transition: 'all 0.18s ease',
                    minWidth: 140,
                    outline: 'none',
                    textAlign: 'left',
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = cfg.accent;
                      e.currentTarget.style.boxShadow = `0 4px 14px -4px ${cfg.glow}`;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
                    <div style={{ fontSize: 12, color: isActive ? cfg.accent : T.slate, fontWeight: 700 }}>
                      {typeVal}
                    </div>
                    <ChevronRight size={12} strokeWidth={2.5} color={isActive ? cfg.accent : T.slate} style={{ transform: isActive ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
                  </div>
                  <div style={{
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 24, fontWeight: 800,
                    color: isActive ? cfg.accent : T.blue900,
                    letterSpacing: '-0.02em', lineHeight: 1,
                  }} title={Number(cnt || 0).toLocaleString()}>
                    {formatCompactNumber(cnt)}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail panel for selected type */}
      {selectedType && (
        <div style={{ borderTop: `1.5px solid ${T.blue100}` }}>
          <ConsentTypeDetailPanel
            role={role}
            typeVal={selectedType}
            rows={selectedTypeRows}
            tenantId={tenantId}
            onSelect={onSelect}
            onClose={() => setSelectedType(null)}
            typeStats={Array.isArray(typeStats) ? typeStats.find(item => item.type === selectedType) : null}
          />
          {typeRowsLoading && <div style={{ padding: '8px 22px', color: T.slate, fontSize: 12 }}>Loading matching records...</div>}
        </div>
      )}
    </div>
  );
}

/* ── Page Guide Modal ─────────────────────────── */
function GuideModal({ steps, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(10,22,40,0.55)',
          backdropFilter: 'blur(4px)',
          zIndex: 1000,
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '95%',
          maxWidth: 620,
          maxHeight: '85vh',
          background: '#f0f4fc',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 20,
          boxShadow: '0 24px 80px rgba(10,22,40,0.28)',
          animation: 'co-modal-slide-in 0.28s cubic-bezier(0.34,1.1,0.64,1) both',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          background: SHARED_GRADIENT,
          padding: '18px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
          position: 'sticky', top: 0, zIndex: 10,
          boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
        }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.70)', marginBottom: 2 }}>
              Help
            </div>
            <div style={{ fontFamily: "'Sora', sans-serif", fontSize: 19, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
              Page Guide
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 34, height: 34, borderRadius: 9,
              border: '1.5px solid rgba(255,255,255,0.3)',
              background: 'rgba(255,255,255,0.15)',
              color: '#fff', fontSize: 15, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s ease', flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.30)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
            aria-label="Close"
          >
            &#10005;
          </button>
        </div>

        <div style={{ padding: '20px 24px', overflowY: 'auto' }}>
          {steps.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: 14,
              padding: '14px 0',
              borderBottom: i < steps.length - 1 ? `1px solid ${T.blue100}` : 'none',
            }}>
              <div style={{
                flexShrink: 0, width: 30, height: 30, borderRadius: '50%',
                background: SHARED_CHIP_BG, color: SHARED_ACCENT, fontWeight: 800, fontSize: 13,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1.5px solid ${SHARED_CHIP_BORDER}`,
              }}>{i + 1}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.blue900, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {s.icon && <span style={{ fontSize: 16 }}>{s.icon}</span>}
                  <span>{s.title}</span>
                </div>
                <div style={{ fontSize: 13, color: T.slate, lineHeight: 1.6 }}>{s.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* ── Consent Types Modal ──────────────────────── */
function ConsentTypesModal({ role, rows, tenantId, onSelect, onClose, typeStats }) {
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG['Employee'];

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(10,22,40,0.55)',
          backdropFilter: 'blur(4px)',
          zIndex: 1000,
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '95%',
          maxWidth: 860,
          maxHeight: '92vh',
          background: '#f0f4fc',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 20,
          boxShadow: '0 24px 80px rgba(10,22,40,0.28)',
          animation: 'co-modal-slide-in 0.28s cubic-bezier(0.34,1.1,0.64,1) both',
          overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          background: cfg.gradient,
          padding: '20px 28px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
          position: 'sticky', top: 0, zIndex: 10,
          boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 13,
              background: 'rgba(255,255,255,0.20)',
              border: '1px solid rgba(255,255,255,0.30)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', flexShrink: 0,
            }}>
              {cfg.icon}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.70)', marginBottom: 2 }}>
                Consent types
              </div>
              <div style={{ fontFamily: "'Sora', sans-serif", fontSize: 20, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                {cfg.display || role}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 38, height: 38, borderRadius: 10,
              border: '1.5px solid rgba(255,255,255,0.30)',
              background: 'rgba(255,255,255,0.15)',
              color: '#fff', fontSize: 16, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s ease', flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.30)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
            aria-label="Close"
          >
            &#10005;
          </button>
        </div>
        <div style={{ flex: 1, padding: '20px 28px 48px' }}>
          <ConsentTypesPanel
            role={role}
            rows={rows}
            tenantId={tenantId}
            onSelect={(payload) => { onSelect?.(payload); onClose(); }}
            onClose={onClose}
            typeStats={typeStats}
            inModal
          />
        </div>
      </div>
    </>
  );
}

/* ── Main Page ────────────────────────────────── */
function ConsentOverviewPage({ onSelect, user }) {
  const tenantId = getTenantId(user);
  const cacheKey = createSessionCacheKey('consent-overview', null, { tenantId });
  const [cachedData] = useState(() => readSessionCache(cacheKey));
  const [overall, setOverall] = useState(() => cachedData?.overall || emptyStats());
  const [byRole, setByRole] = useState(() => cachedData?.byRole || { Employee: emptyStats(), Vendor: emptyStats(), Partners: emptyStats(), Customer: emptyStats() });
  const [loading, setLoading] = useState(() => !cachedData);
  const [rows, setRows] = useState(() => cachedData?.rows || []);
  const [selectedRole, setSelectedRole] = useState(null); // 'Employee' | 'Vendor' | 'Partners' | 'Customer' | null
  const [modalRole, setModalRole] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!tenantId) throw new Error('Tenant context is required to load consent overview');
        const params = { tenant_id: tenantId, dashboard: true, page: 1, page_size: 100, expiring_days: 7 };
        const [rowsRes, statsRes] = await Promise.all([
          axios.get(`${API_BASE}/user/consent`, { params, skipLoading: true }),
          axios.get(`${API_BASE}/user/consent/stats`, { params: { tenant_id: tenantId, expiry_days: 7 }, skipLoading: true }),
        ]);
        const stats = statsRes.data || rowsRes.data?.stats || {};
        const allRows = Array.isArray(rowsRes.data?.consents) ? rowsRes.data.consents : [];
        const fallbackRoleStats = buildRoleStatsFromRows(allRows, tenantId);
        const ov = {
          ...emptyStats(),
          total: Number(stats.total || rowsRes.data?.stats?.total || 0),
          accepted: Number(stats.accepted || rowsRes.data?.stats?.accepted || 0),
          declined: Number(stats.declined || rowsRes.data?.stats?.declined || 0),
          pending: Number(stats.pending || rowsRes.data?.stats?.pending || 0),
          revoked: Number(stats.revoked || rowsRes.data?.stats?.revoked || 0),
          reconsent: Number(stats.reconsent || rowsRes.data?.stats?.reconsent || 0),
          expiring: Number(stats.expiring_soon ?? stats.expiring ?? rowsRes.data?.stats?.expiring_soon ?? rowsRes.data?.stats?.expiring ?? 0),
          expired: Number(stats.expired ?? rowsRes.data?.stats?.expired ?? 0),
        };
        const br = { Employee: emptyStats(), Vendor: emptyStats(), Partners: emptyStats(), Customer: emptyStats() };
        const roleMap = stats.roles || rowsRes.data?.stats?.roles || {};
        for (const role of Object.keys(br)) {
          const roleStats = roleMap[role] || {};
          const source = {
            total: Number(roleStats.total ?? fallbackRoleStats[role]?.total ?? 0),
            accepted: Number(roleStats.accepted ?? fallbackRoleStats[role]?.accepted ?? 0),
            declined: Number(roleStats.declined ?? fallbackRoleStats[role]?.declined ?? 0),
            pending: Number(roleStats.pending ?? fallbackRoleStats[role]?.pending ?? 0),
            revoked: Number(roleStats.revoked ?? fallbackRoleStats[role]?.revoked ?? 0),
            reconsent: Number(roleStats.reconsent ?? fallbackRoleStats[role]?.reconsent ?? 0),
            expiring: Number(roleStats.expiring_soon ?? roleStats.expiring ?? fallbackRoleStats[role]?.expiring ?? 0),
            expired: Number(roleStats.expired ?? fallbackRoleStats[role]?.expired ?? 0),
            types: Array.isArray(roleStats.types) ? roleStats.types : null,
          };
          br[role] = source;
        }

        if (mounted) {
          setOverall(ov);
          setByRole(br);
          setRows(allRows);
          writeSessionCache(cacheKey, { overall: ov, byRole: br, rows: allRows });
        }
      } catch (e) {
        console.warn('Failed to fetch consents for overview', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [tenantId, cacheKey]);

  const roleNormMap = { Employee: 'employee', Vendor: 'vendor', Partners: 'partner', Customer: 'customer' };
  const grandTotal = Object.values(byRole).reduce((s, b) => s + (b.total || 0), 0);

  return (
    <div className="co-root" style={{
      fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
      minHeight: '100vh',
      background: '#f0f4fc',
      padding: '24px 34px 72px',
      boxSizing: 'border-box',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');

        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes co-modal-slide-in {
          from { transform: translate(-50%, -44%) scale(0.96); opacity: 0; }
          to   { transform: translate(-50%, -50%) scale(1);    opacity: 1; }
        }
        @keyframes co-fade-up {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0);    }
        }

        .co-header-banner {
          background: #ffffff;
          border-radius: 24px;
          padding: 24px;
          margin-bottom: 28px;
          position: relative;
          overflow: hidden;
          animation: co-fade-up 0.5s ease both;
          border: 1px solid #e8eef7;
        }
        .co-title {
          font-family: 'Sora', sans-serif;
          font-size: 30px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
          margin: 0 0 6px;
          line-height: 1.1;
        }
        .co-cards-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 22px;
          align-items: stretch;
          animation: co-fade-up 0.5s ease 0.1s both;
        }
        .co-section-title {
          font-family: 'Sora', sans-serif;
          font-size: 16px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: -0.01em;
        }

        @media (max-width: 1200px) {
          .co-cards-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 640px) {
          .co-cards-grid { grid-template-columns: 1fr !important; }
          .co-root { padding: 16px 12px 48px !important; }
          .co-header-banner { padding: 18px 14px !important; }
        }
      `}</style>

      {/* ── Header: title + guide on top, stat cards full width below ── */}
      <div className="co-header-banner">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h1 className="co-title" style={{ margin: 0 }}>Consent Overview</h1>
          <button
            onClick={() => setGuideOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 9,
              background: SHARED_CHIP_BG, color: SHARED_ACCENT,
              border: `1.5px solid ${SHARED_CHIP_BORDER}`,
              fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              transition: 'all 0.15s ease', flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = SHARED_ACCENT; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = SHARED_CHIP_BG; e.currentTarget.style.color = SHARED_ACCENT; }}
          >
            📖 Page Guide
          </button>
        </div>
        <p style={{ fontSize: 14, color: '#64748b', margin: '6px 0 18px', lineHeight: 1.6 }}>
          Track, manage, and review all consent records across your organization.
        </p>
        <StatButtons
          stats={overall}
          loading={loading}
          onStatClick={(key) => {
            if (key === 'total') return onSelect?.(null);
            if (key === 'expiring') return onSelect?.({ expiringSoon: true, days: 7 });
            if (key === 'expired') return onSelect?.({ expiredOnly: true });
            return onSelect?.({ status: key });
          }}
        />
      </div>

      {/* ── Section label ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div className="co-section-title">Consent Categories</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {MIX_ORDER.map((k) => (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.slate }}>
              <i style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_META[k].color, display: 'inline-block' }} />
              {STATUS_META[k].label}
            </span>
          ))}
          <span style={{ fontSize: 12, color: T.slate, fontWeight: 600 }} title={`${grandTotal.toLocaleString()} total records`}>
            {formatCompactNumber(grandTotal)} total records
          </span>
        </div>
      </div>

      {/* ── Cards Grid — 4 fixed categories ── */}
      <div className="co-cards-grid">
        {(['Employee', 'Vendor', 'Partners', 'Customer']).map((role, i) => {
          const norm = roleNormMap[role];
          return (
            <div key={role} style={{ animation: `co-fade-up 0.45s ease ${0.08 + i * 0.10}s both` }}>
              <ConsentCard
                role={role}
                stats={byRole[role]}
                loading={loading}
                onClick={(clickedRole) => {
                  setSelectedRole(prev => prev === clickedRole ? null : clickedRole);
                }}
                onViewConsents={(clickedRole) => {
                  setModalRole(clickedRole);
                }}
                onFilterClick={(statusKey) => {
                  let payload;
                  if (statusKey === 'total') payload = { role: norm, dedupe: true };
                  else if (statusKey === 'expiring') payload = { role: norm, expiringSoon: true, days: 7, dedupe: true };
                  else if (statusKey === 'expired') payload = { role: norm, expiredOnly: true, dedupe: true };
                  else payload = { role: norm, status: statusKey, dedupe: true };
                  onSelect?.(payload);
                }}
              />
            </div>
          );
        })}
      </div>

      {/* ── Inline panel (card click) ── */}
      {selectedRole && (
        <ConsentTypesPanel
          role={selectedRole}
          rows={rows}
          tenantId={tenantId}
          onSelect={onSelect}
          typeStats={byRole[selectedRole]?.types}
          onClose={() => setSelectedRole(null)}
        />
      )}

      {/* ── Full-page slide-in modal (View consents button) ── */}
      {modalRole && (
        <ConsentTypesModal
          role={modalRole}
          rows={rows}
          tenantId={tenantId}
          onSelect={onSelect}
          typeStats={byRole[modalRole]?.types}
          onClose={() => setModalRole(null)}
        />
      )}
      {guideOpen && (
        <GuideModal
          steps={GUIDE_STEPS}
          onClose={() => setGuideOpen(false)}
        />
      )}
    </div>
  );
}

export default ConsentOverviewPage;